import { Box, Typography, TextField } from '@mui/material';
import { mask, unmask } from 'mask-fn';
import React, { useState } from 'react';
import Cards, { Focused } from 'react-credit-cards';
import 'react-credit-cards/lib/styles-compiled.css';
import { useTranslation } from 'react-i18next';
import { ApiCreditCardForm } from '../api';

type FieldName = 'ccHolderName' | 'ccNo' | 'expiry' | 'cvv';

export type CreditCardFormProps = {
  values?: ApiCreditCardForm;
  errors?: {
    ccNo?: string | null;
  };
  onChange: (field: FieldName, value: string) => void;
};

export function CreditCardForm({
  errors,
  values,
  onChange,
}: CreditCardFormProps) {
  const [focusField, setFocusField] = useState<Focused>();
  const { t } = useTranslation();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const field = e.target.name as FieldName;
    const value = ['ccNo', 'expiry'].includes(field)
      ? unmask(e.target.value)
      : e.target.value;
    onChange(field, value);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: [null, null, 'flex'] }}>
        <Box sx={{ mr: 4, flex: 1 }}>
          <Box sx={{ mb: 2 }}>
            <TextField
              type="text"
              size="small"
              name="ccNo"
              label={t('creditCardForm.ccNo')}
              placeholder={t('creditCardForm.ccNoPlaceholder')}
              value={mask(values?.ccNo || '', '9999 9999 9999 9999')}
              onChange={handleChange}
              onFocus={() => setFocusField('number')}
              onBlur={() => setFocusField(undefined)}
              sx={{ mb: 2, width: '100%', backgroundColor: '#FAFAFA' }}
              required
            />
            {errors?.ccNo && (
              <Typography
                sx={{
                  fontSize: 'small',
                  color: 'danger',
                  mt: 1,
                  display: 'block',
                }}
              >
                {errors.ccNo}
              </Typography>
            )}
          </Box>
          <TextField
            type="text"
            size="small"
            name="ccHolderName"
            label={t('creditCardForm.ccHolderName')}
            placeholder={t('creditCardForm.ccHolderNamePlaceholder')}
            value={values?.ccHolderName}
            onChange={handleChange}
            onFocus={() => setFocusField('name')}
            onBlur={() => setFocusField(undefined)}
            sx={{ mb: 4, width: '100%', backgroundColor: '#FAFAFA' }}
            required
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <TextField
              type="text"
              size="small"
              name="expiry"
              label={t('creditCardForm.expiry')}
              placeholder={t('creditCardForm.expiryPlaceholder')}
              value={`${mask(values?.expiry || '', '99/99')}`}
              onChange={handleChange}
              onFocus={() => setFocusField('expiry')}
              onBlur={() => setFocusField(undefined)}
              sx={{ width: '55%', backgroundColor: '#FAFAFA' }}
              required
            />
            <TextField
              type="password"
              size="small"
              name="cvv"
              label={t('creditCardForm.cvv')}
              placeholder={t('creditCardForm.cvvPlaceholder')}
              // maxLength={4}
              value={values?.cvv}
              onChange={handleChange}
              onFocus={() => setFocusField('cvc')}
              onBlur={() => setFocusField(undefined)}
              sx={{ width: '45%', backgroundColor: '#FAFAFA' }}
              required
            />
          </Box>
        </Box>
        <Box sx={{ flex: 1, mt: [2, 2, 0] }}>
          <Cards
            cvc={values?.cvv || ''}
            focused={focusField}
            name={values?.ccHolderName || ''}
            number={values?.ccNo || ''}
            expiry={values?.expiry || ''}
          />
        </Box>
      </Box>
    </Box>
  );
}
